import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { todayDateOnly } from '../../core/datetime/ist';
import { AuthService } from '../../core/services/auth.service';
import { CaseRecord, OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';

export interface CaseAttachment {
  id: string;
  fileName: string;
  kind: string;
  sizeBytes?: number;
  uploadedAt?: string;
}

@Component({
  selector: 'app-cases',
  standalone: true,
  imports: [DatePipe, FormsModule],
  templateUrl: './cases.component.html',
  styleUrl: './cases.component.scss',
})
export class CasesComponent {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  readonly selectedId = signal<string | number>('');
  readonly note = signal('');
  readonly message = signal<string | null>(null);
  readonly busy = signal(false);
  readonly uploadBusy = signal(false);
  readonly attachments = signal<CaseAttachment[]>([]);

  readonly canSubmitReport = computed(() => this.rbac.canWorkflowAction('Submit Report'));

  readonly list = computed(() => {
    const all = this.ops.caseList();
    if (!this.rbac.isAssigneeScoped()) return all;
    const myId = this.auth.officer()?.id;
    return all.filter((c) => String(c.assignedOfficerRef ?? '') === String(myId ?? ''));
  });
  readonly selected = computed((): CaseRecord | undefined => {
    const list = this.list();
    if (!list.length) return undefined;
    return list.find((c) => String(c.id) === String(this.selectedId())) ?? list[0];
  });

  constructor() {
    this.selectedId.set(this.route.snapshot.queryParamMap.get('case') || '');
    effect(() => {
      const item = this.selected();
      if (item) void this.loadAttachments(item.id);
      else this.attachments.set([]);
    });
  }

  statusTone(status?: string | null): string {
    const s = (status || '').toLowerCase();
    if (s === 'closed' || s === 'resolved') return 'tone-done';
    if (s === 'open' || s === 'in progress' || s === 'on hold') return 'tone-active';
    return 'tone-neutral';
  }

  private daysUntil(due?: string | null): number | null {
    if (!due) return null;
    const dueDay = String(due).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDay)) return null;
    const today = todayDateOnly();
    const t = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
    const d = Date.UTC(+dueDay.slice(0, 4), +dueDay.slice(5, 7) - 1, +dueDay.slice(8, 10));
    return Math.round((d - t) / 86400000);
  }

  dueUrgent(due?: string | null): boolean {
    const left = this.daysUntil(due);
    return left != null && left <= 1;
  }

  dueLabel(due?: string | null): string {
    const left = this.daysUntil(due);
    if (left == null) return '';
    if (left < 0) return `${Math.abs(left)}d overdue`;
    if (left === 0) return 'Due today';
    if (left === 1) return '1 day left';
    return `${left} days left`;
  }

  fileKindClass(kind?: string): string {
    const k = (kind || '').toLowerCase();
    if (k === 'pdf') return 'is-pdf';
    if (k === 'doc' || k === 'docx') return 'is-doc';
    return 'is-img';
  }

  formatSize(bytes?: number): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private async loadAttachments(caseId: string | number): Promise<void> {
    try {
      const items = await this.ops.listComplaintDocuments(caseId);
      this.attachments.set(
        items.map((d) => ({
          id: d.id,
          fileName: d.fileName,
          kind: d.kind || (d.fileName?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'),
          sizeBytes: d.sizeBytes,
          uploadedAt: d.uploadedAt,
        })),
      );
    } catch {
      this.attachments.set([]);
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const item = this.selected();
    if (!file || !item) return;

    const allowed =
      /^(application\/pdf|image\/(jpeg|jpg|png|webp|gif)|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document)$/i.test(
        file.type,
      ) || /\.(pdf|png|jpe?g|webp|gif|docx?)$/i.test(file.name);
    if (!allowed) {
      this.message.set('Upload PDF, Word (.doc/.docx), or image only.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.message.set('File too large (max 5 MB).');
      return;
    }

    this.uploadBusy.set(true);
    this.message.set(null);
    try {
      const contentBase64 = await this.readFileBase64(file);
      await this.ops.uploadCaseAttachment(item.id, {
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64,
      });
      await this.loadAttachments(item.id);
      this.message.set(`Uploaded “${file.name}”.`);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      this.uploadBusy.set(false);
    }
  }

  private readFileBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const b64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(b64 || '');
      };
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
  }

  async addNote(): Promise<void> {
    const item = this.selected();
    if (!item || !this.note().trim()) return;
    this.busy.set(true);
    try {
      await this.ops.addCaseNote(item.id, this.note().trim());
      this.message.set('Investigation note added.');
      this.note.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      this.busy.set(false);
    }
  }

  async submitReport(): Promise<void> {
    if (!this.canSubmitReport()) {
      this.message.set('Your desk role cannot submit investigation reports.');
      return;
    }
    const item = this.selected();
    if (!item) return;
    const text = this.note().trim() || item.notes || 'Investigation completed.';
    this.busy.set(true);
    try {
      this.message.set(await this.ops.submitInvestigationReport(item.id, text));
      this.note.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Submit report failed');
    } finally {
      this.busy.set(false);
    }
  }
}
